import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { purchasesService } from '../services/purchasesService';
import { reviewsService } from '../services/reviewsService';
import { useNavigate } from 'react-router-dom';

export default function PurchaseHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewedItems, setReviewedItems] = useState(new Set());

  useEffect(() => {
    loadPurchases();
    checkReviewedItems();
  }, [user]);

  const checkReviewedItems = async () => {
    if (!user) return;
    
    try {
      // Check which items the user has already reviewed
      const reviewedSet = new Set();
      for (const purchase of purchases) {
        if (purchase.listing_id) {
          const hasReviewed = await reviewsService.hasUserReviewedForListing(
            user.id, 
            purchase.seller_id, 
            purchase.listing_id
          );
          if (hasReviewed) {
            reviewedSet.add(purchase.listing_id);
          }
        }
      }
      setReviewedItems(reviewedSet);
    } catch (error) {
      console.error('Error checking reviewed items:', error);
    }
  };

  const loadPurchases = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      console.log('Loading purchase history for user:', user.id);
      const data = await purchasesService.getUserPurchases(user.id);
      console.log('Purchase history data:', data);
      setPurchases(data);
      
      // Check reviewed items after loading purchases
      setTimeout(() => checkReviewedItems(), 100);
    } catch (err) {
      console.error('Error loading purchases:', err);
      setError('Failed to load purchase history');
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (purchase) => {
    // Navigate to the listing detail page
    navigate(`/listing/${purchase.listing_id}`);
  };

  const handleReviewClick = (e, purchase) => {
    e.stopPropagation(); // Prevent item click
    // Navigate to the listing detail page to leave a review
    navigate(`/listing/${purchase.listing_id}?review=true`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="container-center mx-auto max-w-2xl">
        <div className="py-8">
          <h1 className="text-responsive-xl font-bold text-slate-900 mb-6">Purchase History</h1>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/40">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-lg loading-skeleton"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded loading-skeleton mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded loading-skeleton w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-center mx-auto max-w-2xl">
        <div className="py-8">
          <h1 className="text-responsive-xl font-bold text-slate-900 mb-6">Purchase History</h1>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadPurchases}
              className="mt-2 text-red-600 hover:text-red-800 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-center mx-auto max-w-2xl">
      <div className="py-6">
        <h1 className="text-responsive-xl font-bold text-slate-900 mb-6">Purchase History</h1>

        {purchases.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No purchases yet</h2>
            <p className="text-slate-500 mb-6">
              Items you buy will appear here.
            </p>
            <a
              href="/browse"
              className="btn-primary inline-flex items-center"
            >
              Browse Items
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase) => (
              <div key={purchase.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/40 hover:shadow-lg transition-all duration-200">
                <div className="flex items-center space-x-4">
                  {/* Item Image - Clickable */}
                  <div 
                    className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => handleItemClick(purchase)}
                  >
                    {purchase.listing?.photos?.[0] ? (
                      <img
                        src={purchase.listing.photos[0]}
                        alt={purchase.listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        📦
                      </div>
                    )}
                  </div>

                  {/* Item Details - Clickable */}
                  <div 
                    className="flex-1 min-w-0 cursor-pointer hover:bg-slate-50 rounded-lg p-2 -m-2 transition-colors"
                    onClick={() => handleItemClick(purchase)}
                  >
                    <h3 className="font-semibold text-slate-900 truncate">
                      {purchase.listing?.title || 'Unknown Item'}
                    </h3>
                    <p className="text-sm text-slate-600 mb-1">
                      From: {purchase.seller?.name || 'Unknown Seller'}
                    </p>
                    <p className="text-sm text-slate-500">
                      Purchased on {formatDate(purchase.purchase_date)}
                    </p>
                  </div>

                  {/* Price and Review Button */}
                  <div className="text-right flex-shrink-0 flex flex-col items-end space-y-2">
                    <div>
                      <div className="text-lg font-bold text-green-600">
                        ${purchase.listing?.price_cents ? (purchase.listing.price_cents / 100).toFixed(2) : '0.00'}
                      </div>
                      <div className="text-xs text-slate-500">CAD</div>
                    </div>
                    
                    {/* Review Button */}
                    {reviewedItems.has(purchase.listing_id) ? (
                      <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Reviewed
                      </div>
                    ) : (
                      <button
                        onClick={(e) => handleReviewClick(e, purchase)}
                        className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-full font-medium transition-colors"
                      >
                        Leave Review
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
